import React from 'react';
import {List, useRedirect, useTranslate, usePermissions} from 'react-admin';
import {Box, Typography} from '@mui/material';
import {AddRounded} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import StatamicListPage from '../components/statamic/StatamicListPage';
import PrimaryButton from '../components/statamic/PrimaryButton';
import StatusDot from '../components/statamic/StatusDot';
import StatusPill from '../components/statamic/StatusPill';
import MonoChip from '../components/statamic/MonoChip';
import hasPermission from '../auth/hasPermission';

const TextCell = ({value}) => (
    <Typography
        sx={{
            fontSize: `${t.type.body.size}px`,
            color: value ? t.color.text.secondary : t.color.text.muted,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        }}
    >
        {value ?? '—'}
    </Typography>
);

const CharityCell = ({record}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 0}}>
        <StatusDot variant={record.export_active ? 'success' : 'neutral'}/>
        <Typography
            sx={{
                fontSize: `${t.type.bodyStrong.size}px`,
                fontWeight: t.type.bodyStrong.weight,
                color: t.color.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}
        >
            {record.parent_charity_name || '—'}
        </Typography>
    </Box>
);

const ExportSettingsListStatamicInner = () => {
    const redirect = useRedirect();
    const translate = useTranslate();
    const {permissions} = usePermissions();

    const canCreate = !!hasPermission(permissions, ['create'], 'exportSettings');

    const columns = [
        {
            key: 'charity', label: translate('Charity'), width: 220,
            render: (r) => <CharityCell record={r}/>,
        },
        {
            key: 'endpoint', label: translate('Api endpoint'),
            render: (r) => (
                <Typography sx={{fontSize: `${t.type.caption.size}px`, fontFamily: t.font.mono, color: t.color.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                    {r.api_endpoint ?? '—'}
                </Typography>
            ),
        },
        {
            key: 'ledger', label: translate('Ledger ID'), width: 120,
            render: (r) => r.ledger_id != null ? <MonoChip>{r.ledger_id}</MonoChip> : <TextCell value={null}/>,
        },
        {
            key: 'contract', label: translate('Contract ID'), width: 120,
            render: (r) => r.contract_id != null ? <MonoChip>{r.contract_id}</MonoChip> : <TextCell value={null}/>,
        },
        {
            key: 'import_user', label: translate('Import username'), width: 160,
            render: (r) => <TextCell value={r.import_user_name}/>,
        },
        {
            key: 'active', label: translate('Is active'), width: 110,
            render: (r) => (
                <StatusPill variant={r.export_active ? 'success' : 'neutral'}>
                    {r.export_active ? translate('Active') : translate('Inactive')}
                </StatusPill>
            ),
        },
    ];

    const rowActions = (record) => [
        {label: translate('Edit'), onClick: () => redirect('edit', 'exportSettings', record.id)},
    ];

    return (
        <StatamicListPage
            scoped="umbrellaOnly"
            title={translate('Export Settings')}
            subtitle="OVAS export integration per umbrella"
            headerActions={
                canCreate ? (
                    <PrimaryButton
                        variant="primary"
                        startIcon={<AddRounded sx={{fontSize: 18}}/>}
                        onClick={() => redirect('create', 'exportSettings')}
                    >
                        Create export settings
                    </PrimaryButton>
                ) : null
            }
            columns={columns}
            rowActions={rowActions}
            onRowClick={(record) => redirect('edit', 'exportSettings', record.id)}
        />
    );
};

const ExportSettingsListStatamic = (props) => (
    <List
        {...props}
        perPage={25}
        actions={false}
        pagination={false}
        component={Box}
        sx={{
            backgroundColor: t.color.page,
            '& .RaList-main': {backgroundColor: t.color.page},
        }}
    >
        <ExportSettingsListStatamicInner/>
    </List>
);

export default ExportSettingsListStatamic;
